/**
 * "Chart only" view (owner 2026-10-05: "a charting only mode where it hides everything like dom
 * and accounts and only shows the chart"). Pure rules; the web app's workspace/chartOnly.ts applies
 * them to the dock without touching the saved layout.
 *
 * - A group with no chart module is hidden (a docked group gives its room to its neighbours, so
 *   the charts keep the owner's arrangement and take the space; a floating one is hidden).
 * - A group that holds a chart and other modules stays; if a non-chart tab is in front, the group's
 *   first chart is brought to the front, and the non-chart tabs are hidden.
 * - Pop-out windows are separate windows and are left as they are.
 *
 * Leaving shows exactly what was hidden and puts the previous tabs back in front.
 */

/** dockview group location type: "grid", "floating", "popout" (or "edge"). */
export type ChartOnlyGroup = {
  id: string;
  location: string;
  /** The group's modules in tab order. `kind` is the module kind ("chart", "dom", …). */
  panels: readonly { id: string; kind: string | undefined }[];
  activePanelId: string | null;
};

export type ChartOnlyPlan = {
  /** Groups to hide (no chart in them), in the order given. */
  hideGroups: string[];
  /** Mixed groups whose front tab is not a chart: bring this chart to the front. */
  front: { groupId: string; panelId: string }[];
  /** Non-chart tabs in the groups that stay. */
  hideTabs: string[];
  /** Chart modules shown in the main window (docked or floating). */
  charts: number;
};

const isChart = (kind: string | undefined): boolean => kind === "chart";

/** Groups in the main window: docked or floating (pop-outs are their own windows). */
const inMainWindow = (location: string): boolean => location === "grid" || location === "floating";

export function planChartOnly(groups: readonly ChartOnlyGroup[]): ChartOnlyPlan {
  const plan: ChartOnlyPlan = { hideGroups: [], front: [], hideTabs: [], charts: 0 };
  for (const g of groups) {
    if (!inMainWindow(g.location)) continue;
    const charts = g.panels.filter((p) => isChart(p.kind));
    if (charts.length === 0) {
      plan.hideGroups.push(g.id);
      continue;
    }
    plan.charts += charts.length;
    for (const p of g.panels) if (!isChart(p.kind)) plan.hideTabs.push(p.id);
    const active = g.panels.find((p) => p.id === g.activePanelId);
    const first = charts[0];
    if (first && (active === undefined || !isChart(active.kind)))
      plan.front.push({ groupId: g.id, panelId: first.id });
  }
  return plan;
}

/** Chart modules in the main window (the empty state shows when there are none). */
export function chartOnlyChartCount(groups: readonly ChartOnlyGroup[]): number {
  return groups
    .filter((g) => inMainWindow(g.location))
    .reduce((n, g) => n + g.panels.filter((p) => isChart(p.kind)).length, 0);
}

/** A node of the dock's serialized grid (dockview `SerializedGridObject`; a leaf's data has the group id). */
export type ChartOnlyGridNode = {
  type: "leaf" | "branch";
  data: unknown;
  size?: number;
  visible?: boolean;
};
export type ChartOnlyBox = { width: number; height: number };
export type GridOrientation = "HORIZONTAL" | "VERTICAL";

const leafId = (n: ChartOnlyGridNode): string | undefined =>
  (n.data as { id?: unknown } | null)?.id as string;
const children = (n: ChartOnlyGridNode): ChartOnlyGridNode[] =>
  n.type === "branch" && Array.isArray(n.data) ? (n.data as ChartOnlyGridNode[]) : [];

function shown(n: ChartOnlyGridNode, hidden: ReadonlySet<string>): boolean {
  if (n.visible === false) return false;
  if (n.type === "leaf") {
    const id = leafId(n);
    return id !== undefined && !hidden.has(id);
  }
  return children(n).some((c) => shown(c, hidden));
}

/**
 * The size of every docked group once the `hidden` ones are out, keeping the owner's arrangement
 * and proportions: in each row / column, the groups that still show share its length in the
 * proportion of their saved sizes (two charts at 430 | 563 px stay 43 : 56). With nothing hidden it
 * gives back the saved sizes exactly (how leaving Chart only restores them). `orientation` and `box`
 * are the grid's (dockview: a HORIZONTAL branch lays its children side by side, and a child's
 * `size` is its width there; orientations alternate by depth).
 */
export function chartOnlySizes(
  root: ChartOnlyGridNode,
  orientation: GridOrientation,
  box: ChartOnlyBox,
  hidden: ReadonlySet<string>,
): Map<string, ChartOnlyBox> {
  const out = new Map<string, ChartOnlyBox>();
  const walk = (node: ChartOnlyGridNode, o: GridOrientation, b: ChartOnlyBox): void => {
    if (node.type === "leaf") {
      const id = leafId(node);
      if (id !== undefined) out.set(id, b);
      return;
    }
    const kids = children(node).filter((c) => shown(c, hidden));
    const along = o === "HORIZONTAL" ? b.width : b.height;
    const total = kids.reduce((s, c) => s + Math.max(0, c.size ?? 0), 0);
    let used = 0;
    kids.forEach((c, i) => {
      const share = total > 0 ? Math.max(0, c.size ?? 0) / total : 1 / kids.length;
      const len = i === kids.length - 1 ? along - used : Math.round(share * along);
      used += len;
      walk(
        c,
        o === "HORIZONTAL" ? "VERTICAL" : "HORIZONTAL",
        o === "HORIZONTAL" ? { width: len, height: b.height } : { width: b.width, height: len },
      );
    });
  };
  if (shown(root, hidden)) walk(root, orientation, box);
  return out;
}

/** The per-browser preference (`localStorage`): "1" is on; anything else, or nothing, is off. */
export const CHART_ONLY_KEY = "helm:chart-only";

export function parseChartOnly(raw: string | null | undefined): boolean {
  return raw === "1";
}

export function serializeChartOnly(on: boolean): string {
  return on ? "1" : "0";
}
